import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout/Layout';
import './ActivityLog.css';

const ActivityLog = () => {
  const { getAuthHeaders } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 50,
    total: 0,
    total_pages: 0,
    has_next_page: false,
    has_prev_page: false
  });

  // Filters
  const [filters, setFilters] = useState({
    entity_type: '',
    action_type: '',
    user_id: '',
    start_date: '',
    end_date: '',
    search: ''
  });

  // Load activity logs
  const loadActivityLogs = async (page = 1) => {
    try {
      setLoading(true);
      setError('');

      // Build query params - include page and limit, plus any active filters
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: '50'
      });
      
      // Add filters only if they have values
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value !== '') {
          queryParams.append(key, value);
        }
      });

      const response = await fetch(`/api/activity-logs?${queryParams.toString()}`, {
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setLogs(data.data.logs || []);
        setPagination(data.data.pagination || {
          current_page: 1,
          per_page: 50,
          total: 0,
          total_pages: 0,
          has_next_page: false,
          has_prev_page: false
        });
        console.log('Activity logs loaded:', data.data.logs?.length || 0, 'logs');
      } else {
        if (response.status === 401) {
          localStorage.removeItem('travel-futura-token');
          localStorage.removeItem('travel-futura-user');
          window.location.href = '/login';
          return;
        }
        console.error('Activity log API error:', data);
        setError(data.message || 'Failed to load activity logs');
      }
    } catch (error) {
      console.error('Load activity logs error:', error);
      setError('Failed to load activity logs. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Load all activity logs on component mount
    loadActivityLogs(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Apply filters
  const handleApplyFilters = () => {
    loadActivityLogs(1);
  };

  // Clear filters
  const handleClearFilters = () => {
    setFilters({
      entity_type: '',
      action_type: '',
      user_id: '',
      start_date: '',
      end_date: '',
      search: ''
    });
    setTimeout(() => {
      loadActivityLogs(1);
    }, 100);
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get action badge color
  const getActionBadgeColor = (actionType) => {
    const colors = {
      create: '#10b981',
      update: '#3b82f6',
      delete: '#ef4444',
      toggle: '#f59e0b',
      status_change: '#8b5cf6',
      feature_toggle: '#ec4899',
      top_toggle: '#f97316'
    };
    return colors[actionType] || '#6b7280';
  };

  // Get entity badge color
  const getEntityBadgeColor = (entityType) => {
    const colors = {
      attraction: '#3b82f6',
      event: '#8b5cf6',
      banner: '#10b981',
      home_card: '#f59e0b',
      feedback: '#ec4899',
      admin_user: '#6b7280'
    };
    return colors[entityType] || '#6b7280';
  };

  return (
    <Layout>
      <div className="activity-log-container">
        <div className="activity-log-header">
          <h1>Activity Log</h1>
          <p>Track all changes made by CMS users - Showing all activity logs</p>
        </div>

        {/* Filters */}
        <div className="activity-log-filters">
          <div className="filter-row">
            <div className="filter-group">
              <label>Search</label>
              <input
                type="text"
                name="search"
                value={filters.search}
                onChange={handleFilterChange}
                placeholder="Search by username, entity name, or description..."
              />
            </div>
            <div className="filter-group">
              <label>Entity Type</label>
              <select
                name="entity_type"
                value={filters.entity_type}
                onChange={handleFilterChange}
              >
                <option value="">All Types</option>
                <option value="attraction">Attraction</option>
                <option value="event">Event</option>
                <option value="banner">Banner</option>
                <option value="home_card">Home Card</option>
                <option value="feedback">Feedback</option>
                <option value="admin_user">Admin User</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Action Type</label>
              <select
                name="action_type"
                value={filters.action_type}
                onChange={handleFilterChange}
              >
                <option value="">All Actions</option>
                <option value="create">Create</option>
                <option value="update">Update</option>
                <option value="delete">Delete</option>
                <option value="toggle">Toggle</option>
                <option value="status_change">Status Change</option>
                <option value="feature_toggle">Feature Toggle</option>
                <option value="top_toggle">Top Toggle</option>
              </select>
            </div>
          </div>
          <div className="filter-row">
            <div className="filter-group">
              <label>User ID</label>
              <input
                type="number"
                name="user_id"
                value={filters.user_id}
                onChange={handleFilterChange}
                placeholder="Filter by user ID"
              />
            </div>
            <div className="filter-group">
              <label>Start Date</label>
              <input
                type="date"
                name="start_date"
                value={filters.start_date}
                onChange={handleFilterChange}
              />
            </div>
            <div className="filter-group">
              <label>End Date</label>
              <input
                type="date"
                name="end_date"
                value={filters.end_date}
                onChange={handleFilterChange}
              />
            </div>
          </div>
          <div className="filter-actions">
            <button onClick={handleApplyFilters} className="btn-primary">
              Apply Filters
            </button>
            <button onClick={handleClearFilters} className="btn-secondary">
              Clear Filters
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading activity logs...</p>
          </div>
        ) : (
          <>
            {/* Activity Logs Table */}
            <div className="activity-log-table-container">
              <table className="activity-log-table">
                <thead>
                  <tr>
                    <th>Date & Time</th>
                    <th>User</th>
                    <th>Action</th>
                    <th>Entity</th>
                    <th>Entity Name</th>
                    <th>Description</th>
                    <th>IP Address</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="no-data">
                        No activity logs found
                      </td>
                    </tr>
                  ) : (
                    logs.map((log) => (
                      <tr key={log.id}>
                        <td>{formatDate(log.created_at)}</td>
                        <td>
                          <span className="username-badge">{log.username}</span>
                        </td>
                        <td>
                          <span
                            className="action-badge"
                            style={{ backgroundColor: getActionBadgeColor(log.action_type) }}
                          >
                            {log.action_type}
                          </span>
                        </td>
                        <td>
                          <span
                            className="entity-badge"
                            style={{ backgroundColor: getEntityBadgeColor(log.entity_type) }}
                          >
                            {log.entity_type}
                          </span>
                        </td>
                        <td>{log.entity_name || `ID: ${log.entity_id}`}</td>
                        <td>{log.description || '-'}</td>
                        <td>{log.ip_address || '-'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.total_pages > 1 && (
              <div className="pagination">
                <button
                  onClick={() => loadActivityLogs(pagination.current_page - 1)}
                  disabled={!pagination.has_prev_page}
                  className="pagination-btn"
                >
                  Previous
                </button>
                <span className="pagination-info">
                  Page {pagination.current_page} of {pagination.total_pages} 
                  ({pagination.total} total logs)
                </span>
                <button
                  onClick={() => loadActivityLogs(pagination.current_page + 1)}
                  disabled={!pagination.has_next_page}
                  className="pagination-btn"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default ActivityLog;

